// Warm & Reassuring homepage — real links + live products. Honest copy for an
// early-stage doctor-led clinic (no fabricated stats or reviews).
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useProducts } from '../context/ProductsContext'

// Richer, jewel-toned palette — deeper saturated emerald + warm gold accent
const cream = '#FAF6EE', sand = '#EBE0CE', ink = '#17251D', muted = '#5F6A60'
const emer = '#064E36', emer2 = '#0C7A4E', sage = '#1FA766', sage2 = '#5FD198'
const peach = '#EFB24C', mint = '#DFF3E8'
const serif = "'DM Serif Display',serif", sans = "'DM Sans',sans-serif"
const card = { background: '#fff', borderRadius: 24, border: `1px solid ${sand}`, boxShadow: '0 22px 44px -26px rgba(38,51,43,.28)' }

function Stars({ s = 12 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[0, 1, 2, 3, 4].map(i => (
        <svg key={i} width={s} height={s} viewBox="0 0 24 24" fill="#E9A93B"><path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.8 6 20.4l1.5-6.8L2.3 9l6.9-.7z" /></svg>
      ))}
    </span>
  )
}

function Eyebrow({ children }) {
  return <div style={{ fontFamily: sans, fontSize: '.74rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: sage, marginBottom: '.7rem' }}>{children}</div>
}
function Heading({ eyebrow, title, sub, center }) {
  return (
    <div style={{ textAlign: center ? 'center' : 'left', maxWidth: center ? 640 : 560, margin: center ? '0 auto 3rem' : '0 0 2.5rem' }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem,3.4vw,2.6rem)', lineHeight: 1.12, color: ink, letterSpacing: '-.01em' }}>{title}</h2>
      {sub && <p style={{ fontFamily: sans, fontSize: '1rem', lineHeight: 1.7, color: muted, marginTop: '.8rem' }}>{sub}</p>}
    </div>
  )
}
const Icon = ({ d, s = 22, c = emer2 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{d}</svg>

// ── HERO ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{ position: 'relative', overflow: 'hidden', background: `radial-gradient(1100px 620px at 88% -8%, #DCF1E4 0%, transparent 55%), radial-gradient(900px 520px at -6% 112%, #E7F5EC 0%, transparent 52%), linear-gradient(165deg, #F2FAF1 0%, ${cream} 48%, #FCF1DE 100%)`, padding: '4.5rem 1.5rem 5rem' }}>
      <div style={{ position: 'absolute', top: '-14%', right: '-10%', width: 620, height: 620, borderRadius: '46% 54% 60% 40% / 52% 44% 56% 48%', background: 'radial-gradient(circle at 40% 40%, rgba(31,167,102,.34), transparent 68%)', filter: 'blur(4px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-18%', left: '-12%', width: 500, height: 500, borderRadius: '54% 46% 40% 60% / 46% 56% 44% 54%', background: 'radial-gradient(circle, rgba(6,78,54,.22), transparent 66%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '30%', left: '40%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(239,178,76,.22), transparent 70%)', pointerEvents: 'none' }} />
      <div className="wh-hero" style={{ position: 'relative', zIndex: 1, maxWidth: 1600, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0,1.05fr) minmax(0,.95fr)', gap: '3.5rem', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${sand}`, borderRadius: 999, padding: '.4rem .9rem', boxShadow: '0 6px 18px -10px rgba(38,51,43,.25)', marginBottom: '1.6rem' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: sage }} />
            <span style={{ fontSize: '.72rem', fontWeight: 600, letterSpacing: '.06em', color: emer2, fontFamily: sans }}>Reviewed by real dermatologists</span>
          </div>
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(2.4rem,4.6vw,3.9rem)', lineHeight: 1.06, color: ink, letterSpacing: '-.01em', marginBottom: '1.2rem' }}>
            Skincare that actually<br /><span style={{ color: emer2, fontStyle: 'italic' }}>listens</span> to your skin.
          </h1>
          <p style={{ fontFamily: sans, fontSize: '1.05rem', lineHeight: 1.75, color: muted, maxWidth: 460, marginBottom: '2rem' }}>
            Start with a free skin check, then get a gentle, personalised plan from a certified dermatologist — with the right products delivered to your door. Caring, expert help from just <strong style={{ color: ink, fontWeight: 600 }}>₹500</strong>.
          </p>
          <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', marginBottom: '2.2rem' }}>
            <Link to="/book-appointment" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: `linear-gradient(135deg, ${emer} 0%, ${sage} 100%)`, color: '#fff', border: 'none', borderRadius: 999, padding: '.95rem 1.6rem', fontSize: '.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: sans, boxShadow: '0 18px 34px -10px rgba(31,167,102,.6)', textDecoration: 'none' }}>
              Start free skin check <Icon d={<path d="M5 12h14M13 6l6 6-6 6" />} s={17} c="#fff" />
            </Link>
            <Link to="/shop" style={{ background: '#fff', color: emer, border: `1.5px solid ${sand}`, borderRadius: 999, padding: '.95rem 1.5rem', fontSize: '.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: sans, textDecoration: 'none' }}>Shop products</Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {['Reviewed by a real dermatologist', 'No bots, no auto-diagnosis'].map(t => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#fff', border: `1px solid ${sand}`, borderRadius: 999, padding: '.45rem .85rem', fontSize: '.78rem', fontWeight: 600, color: emer2, fontFamily: sans }}>
                <Icon d={<><circle cx="12" cy="12" r="9" /><path d="M8 12l2.5 2.5L16 9" /></>} s={15} /> {t}
              </span>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', minHeight: 420 }}>
          <div style={{ position: 'relative', borderRadius: 32, overflow: 'hidden', background: `linear-gradient(150deg, #04402B 0%, ${emer} 32%, ${emer2} 64%, ${sage} 100%)`, minHeight: 420, boxShadow: `0 44px 90px -28px rgba(6,78,54,.6)` }}>
            <div style={{ position: 'absolute', top: -40, right: -30, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.16), transparent 70%)' }} />
            <div style={{ position: 'absolute', bottom: -50, left: -40, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(133,201,154,.35), transparent 70%)' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '1.8rem' }}>
              <div style={{ fontSize: '.66rem', letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.6)', marginBottom: 6, fontFamily: sans }}>Your dermatologist</div>
              <div style={{ fontFamily: serif, fontSize: '1.7rem', color: '#fff', lineHeight: 1.15 }}>Gentle, expert care —<br />made just for you.</div>
            </div>
          </div>
          <div className="wh-float" style={{ position: 'absolute', top: 26, left: -18, background: '#fff', borderRadius: 18, padding: '.8rem 1rem', boxShadow: '0 20px 40px -16px rgba(38,51,43,.35)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: mint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>} s={18} /></div>
            <div><div style={{ fontSize: '.7rem', color: muted, fontFamily: sans }}>Online consult</div><div style={{ fontSize: '.95rem', fontWeight: 800, color: ink, fontFamily: sans }}>from ₹500</div></div>
          </div>
          <div className="wh-float" style={{ position: 'absolute', top: -14, right: 22, background: peach, color: '#5A2E1C', borderRadius: 999, padding: '.45rem .9rem', fontSize: '.74rem', fontWeight: 800, boxShadow: '0 14px 26px -12px rgba(231,169,134,.9)', fontFamily: sans }}>Free skin check ✓</div>
          {/* TODO: swap gradient for the real doctor's photo + name */}
          <div className="wh-float" style={{ position: 'absolute', bottom: 20, right: -20, background: '#fff', borderRadius: 20, padding: '.85rem 1rem', boxShadow: '0 24px 46px -18px rgba(38,51,43,.4)', display: 'flex', alignItems: 'center', gap: 11, maxWidth: 250 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#DABFA6,#A8CBB4)', flexShrink: 0 }} />
            <div><div style={{ fontSize: '.86rem', fontWeight: 700, color: ink, fontFamily: sans }}>Your dermatologist</div><div style={{ fontSize: '.72rem', color: muted, fontFamily: sans }}>MD, Dermatology</div></div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── TRUST STRIP ──────────────────────────────────────────────────────────────
function Trust() {
  const items = [['Real', 'Dermatologist, not AI'], ['1,500+', 'Curated products'], ['Free', 'First skin check'], ['₹500', 'Online consultation']]
  return (
    <section style={{ background: '#fff', borderTop: `1px solid ${sand}`, borderBottom: `1px solid ${sand}`, padding: '2rem 1.5rem' }}>
      <div className="wh-trust" style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
        {items.map(([n, l]) => (
          <div key={l} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: serif, fontSize: '1.9rem', color: emer2, lineHeight: 1 }}>{n}</div>
            <div style={{ fontFamily: sans, fontSize: '.8rem', color: muted, marginTop: 5 }}>{l}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── HOW IT WORKS ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: '01', t: 'Free skin check', d: 'Upload a few photos. Our AI highlights concerns for your dermatologist to review — no cost, no pressure.', d2: <><circle cx="12" cy="12" r="9" /><path d="M8 12l2.5 2.5L16 9" /></> },
    { n: '02', t: 'Meet your dermatologist', d: 'A certified doctor reviews your skin and talks with you online — real, human, unhurried care.', d2: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></> },
    { n: '03', t: 'Get your plan', d: 'A gentle, personalised routine with the right products delivered — and reminders so you never miss a step.', d2: <><path d="M20 7L9 18l-5-5" /></> },
  ]
  return (
    <section style={{ background: cream, padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <Heading center eyebrow="How it works" title="Care in three gentle steps" sub="No clinics, no queues. Thoughtful dermatology from the comfort of home." />
        <div className="wh-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1.4rem' }}>
          {steps.map(s => (
            <div key={s.n} style={{ ...card, padding: '2rem 1.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
                <div style={{ width: 50, height: 50, borderRadius: 16, background: mint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={s.d2} s={24} /></div>
                <span style={{ fontFamily: serif, fontSize: '1.6rem', color: sand }}>{s.n}</span>
              </div>
              <div style={{ fontFamily: serif, fontSize: '1.25rem', color: ink, marginBottom: '.5rem' }}>{s.t}</div>
              <p style={{ fontFamily: sans, fontSize: '.92rem', lineHeight: 1.7, color: muted }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── CONCERNS ─────────────────────────────────────────────────────────────────
function Concerns() {
  const items = [
    { t: 'Acne & breakouts', d: <><circle cx="12" cy="12" r="9" /><circle cx="9" cy="10" r="1" /><circle cx="15" cy="13" r="1" /></> },
    { t: 'Pigmentation', d: <><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 000 18" fill={mint} /></> },
    { t: 'Hair fall', d: <><path d="M12 3c4 3 6 7 6 11a6 6 0 01-12 0c0-4 2-8 6-11z" /></> },
    { t: 'Anti-ageing', d: <><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /><circle cx="12" cy="12" r="4" /></> },
    { t: 'Sensitive skin', d: <><path d="M12 21s-7-4.35-7-10a4 4 0 017-2.65A4 4 0 0119 11c0 5.65-7 10-7 10z" /></> },
    { t: 'Dryness & dullness', d: <><path d="M12 2v6M4.9 7l4.2 4.2M2 14h6M19.1 7l-4.2 4.2M22 14h-6M12 22a6 6 0 006-6H6a6 6 0 006 6z" /></> },
  ]
  return (
    <section style={{ background: '#fff', padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <Heading center eyebrow="What we help with" title="Real answers for real skin concerns" />
        <div className="wh-concerns" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1.1rem' }}>
          {items.map(it => (
            <Link key={it.t} to={`/book-appointment?concern=${encodeURIComponent(it.t)}`} className="wh-concern" style={{ display: 'flex', alignItems: 'center', gap: 14, background: cream, border: `1px solid ${sand}`, borderRadius: 18, padding: '1.2rem 1.3rem', textDecoration: 'none' }}>
              <div style={{ width: 46, height: 46, borderRadius: 14, background: '#fff', border: `1px solid ${sand}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon d={it.d} s={22} /></div>
              <span style={{ flex: 1, fontFamily: sans, fontSize: '.98rem', fontWeight: 600, color: ink }}>{it.t}</span>
              <Icon d={<path d="M9 6l6 6-6 6" />} s={16} c={sage2} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── FEATURED PRODUCTS ────────────────────────────────────────────────────────
function Products() {
  const { products } = useProducts()
  const items = (products || []).filter(p => { const u = p.imgs?.[0]; return u && String(u).trim() && String(u) !== 'null' }).slice(0, 4)
  if (!items.length) return null
  return (
    <section style={{ background: cream, padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '2.5rem' }}>
          <div><Eyebrow>Dermatologist-curated</Eyebrow><h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem,3.4vw,2.6rem)', color: ink, lineHeight: 1.12 }}>From our shelves</h2></div>
          <Link to="/shop" style={{ background: '#fff', color: emer, border: `1.5px solid ${sand}`, borderRadius: 999, padding: '.7rem 1.3rem', fontSize: '.9rem', fontWeight: 600, fontFamily: sans, textDecoration: 'none' }}>View all products</Link>
        </div>
        <div className="wh-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1.2rem' }}>
          {items.map(it => (
            <Link key={it.id} to={`/product/${it.id}`} style={{ ...card, overflow: 'hidden', textDecoration: 'none', display: 'block' }}>
              <div style={{ height: 180, background: '#fff', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                <img src={it.imgs[0]} alt={it.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }} />
                <span style={{ position: 'absolute', top: 12, left: 12, background: mint, color: emer2, fontSize: '.66rem', fontWeight: 800, padding: '.25rem .6rem', borderRadius: 999, fontFamily: sans }}>Doctor-picked</span>
              </div>
              <div style={{ padding: '1.1rem 1.1rem 1.3rem', borderTop: `1px solid ${sand}` }}>
                <div style={{ fontFamily: sans, fontSize: '.7rem', color: muted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.05em' }}>{it.brand || 'Revive'}</div>
                <div style={{ fontFamily: sans, fontSize: '.9rem', fontWeight: 600, color: ink, lineHeight: 1.35, minHeight: 40, marginBottom: 4, overflow: 'hidden' }}>{it.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                  <span style={{ fontFamily: serif, fontSize: '1.15rem', color: ink }}>₹{it.price}</span>
                  <span style={{ width: 38, height: 38, borderRadius: 12, background: emer, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>} s={18} c="#fff" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── WHY REVIVE (honest values — no fabricated reviews) ───────────────────────
function Reviews() {
  const items = [
    { t: 'A real dermatologist', d: 'Every skin check and plan is reviewed by a certified doctor — never an automated diagnosis.', i: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></> },
    { t: 'Personalised, not generic', d: 'Your routine is built around your skin and concerns — not a one-size-fits-all template.', i: <><path d="M12 2l2.5 6.5L21 9l-5 4 1.5 7L12 16l-5.5 4L8 13 3 9l6.5-.5z" /></> },
    { t: 'Honest & evidence-based', d: "We recommend what your skin actually needs — and tell you plainly what it doesn't.", i: <><circle cx="12" cy="12" r="9" /><path d="M8 12l2.5 2.5L16 9" /></> },
    { t: 'We stay with you', d: 'Gentle reminders and follow-up care keep your treatment on track, long after the consult.', i: <><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 01-3.4 0" /></> },
  ]
  return (
    <section style={{ background: '#fff', padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <Heading center eyebrow="Why Revive" title="Care you can actually trust" sub="We're a small, doctor-led clinic — which means real attention, not a call-centre." />
        <div className="wh-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1.2rem' }}>
          {items.map(it => (
            <div key={it.t} style={{ ...card, padding: '1.7rem 1.4rem', background: cream }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: '#fff', border: `1px solid ${sand}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.1rem' }}><Icon d={it.i} s={23} /></div>
              <div style={{ fontFamily: serif, fontSize: '1.15rem', color: ink, marginBottom: '.5rem' }}>{it.t}</div>
              <p style={{ fontFamily: sans, fontSize: '.9rem', lineHeight: 1.65, color: muted }}>{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── CTA ──────────────────────────────────────────────────────────────────────
function CTA() {
  return (
    <section style={{ background: cream, padding: '2rem 1.5rem 5rem' }}>
      <div style={{ position: 'relative', overflow: 'hidden', maxWidth: 1000, margin: '0 auto', borderRadius: 32, padding: '3.5rem 2rem', textAlign: 'center', background: `linear-gradient(150deg, #04402B 0%, ${emer} 30%, ${emer2} 62%, ${sage} 100%)`, boxShadow: `0 44px 90px -28px rgba(6,78,54,.6)` }}>
        <div style={{ position: 'absolute', top: -60, right: -40, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle,rgba(133,201,154,.35),transparent 70%)' }} />
        <div style={{ position: 'absolute', bottom: -70, left: -30, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle,rgba(255,255,255,.1),transparent 70%)' }} />
        <div style={{ position: 'relative' }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.9rem,3.6vw,2.8rem)', color: '#fff', lineHeight: 1.12, marginBottom: '.8rem' }}>Ready to care for your skin?</h2>
          <p style={{ fontFamily: sans, fontSize: '1.02rem', color: 'rgba(255,255,255,.8)', maxWidth: 480, margin: '0 auto 1.8rem', lineHeight: 1.7 }}>Start with a free skin check today. A real dermatologist is ready to help.</p>
          <Link to="/book-appointment" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#fff', color: emer, border: 'none', borderRadius: 999, padding: '1rem 1.9rem', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', fontFamily: sans, boxShadow: '0 18px 34px -14px rgba(0,0,0,.35)', textDecoration: 'none' }}>
            Start free skin check <Icon d={<path d="M5 12h14M13 6l6 6-6 6" />} s={18} c={emer} />
          </Link>
        </div>
      </div>
    </section>
  )
}

const responsiveCSS = `
@media (max-width: 960px){
  .wh-hero{grid-template-columns:1fr!important;gap:2.4rem!important}
  .wh-float{display:none!important}
  .wh-4{grid-template-columns:repeat(2,1fr)!important}
  .wh-concerns{grid-template-columns:repeat(2,1fr)!important}
}
@media (max-width: 680px){
  .wh-3{grid-template-columns:1fr!important}
  .wh-concerns{grid-template-columns:1fr!important}
  .wh-trust{grid-template-columns:repeat(2,1fr)!important;gap:1.6rem!important}
  .wh-sec{padding-top:3.2rem!important;padding-bottom:3.2rem!important}
}
@media (max-width: 460px){
  .wh-4{grid-template-columns:1fr!important}
}
/* Scroll reveal (subtle fade + rise, ease-out) — per ui-ux-pro-max motion spec */
.reveal{opacity:0;transform:translateY(22px);transition:opacity .6s ease-out,transform .6s ease-out}
.reveal.in{opacity:1;transform:none}
/* Gentle hover lift on interactive cards */
.wh-concern,.wh-4>a{transition:transform .18s ease,box-shadow .18s ease}
.wh-concern:hover{transform:translateY(-2px);border-color:#85C99A}
.wh-4>a:hover{transform:translateY(-4px);box-shadow:0 30px 55px -28px rgba(38,51,43,.4)}
@media (prefers-reduced-motion: reduce){
  .reveal{opacity:1!important;transform:none!important;transition:none!important}
  .wh-concern,.wh-4>a{transition:none!important}
}
`

export default function WarmHome() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
    document.querySelectorAll('.reveal').forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])
  return (
    <div style={{ background: cream }}>
      <style>{responsiveCSS}</style>
      <Hero />
      <div className="reveal"><Trust /></div>
      <div className="reveal"><HowItWorks /></div>
      <div className="reveal"><Concerns /></div>
      <div className="reveal"><Products /></div>
      <div className="reveal"><Reviews /></div>
      <div className="reveal"><CTA /></div>
    </div>
  )
}
